import { alpha, Theme } from '@mui/material/styles'

import pageBackgroundImage from '../../images/dashboard-background.png'

const useAccountStyles = {
  banner: (theme: Theme) => ({
    margin: theme.spacing(-3, 0, 0, 0),
    width: '100%',
  }),
  bannerImage: {
    position: 'absolute',
    zIndex: -1,
    left: 'calc((100vw - 100%)/-2)',
    top: 0,
    width: '100vw',
    height: '100%',
    opacity: 0.5,
    background: `url(${pageBackgroundImage}), rgba(224, 232, 245, 0.1)`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
  },
  minimizedBannerImage: {
    width: '100%',
    left: 0,
  },
  folderNameWrapper: (theme: Theme) => ({
    [theme.breakpoints.only('xs')]: {
      marginTop: theme.spacing(3),
    },
  }),
  minimizedFolderName: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    marginTop: 0,
  },
  fullHeader: (theme: Theme) => ({
    height: '100%',
    position: 'relative',
    paddingTop: theme.spacing(2),
  }),
  copyIcon: {
    fontSize: '16px',
  },
  copyBtn: (theme: Theme) => ({
    marginLeft: theme.spacing(1),
  }),
  minimizedHeader: (theme: Theme) => ({
    height: '100px',
    position: 'fixed',
    top: '84px',
    width: '100%',
    backgroundColor: theme.palette.common.white,
    zIndex: 100,
    left: 0,
    padding: theme.spacing(2, 3, 0, 3),
  }),
  tabsContainer: (theme: Theme) => ({
    height: '100%',
    [theme.breakpoints.down('md')]: {
      margin: theme.spacing(0, -3, 0, -3),
      padding: theme.spacing(0, 3, 0, 3),
      width: 'calc(100% + 48px)',
    },
    '& .MuiTabs-flexContainer': {
      flexWrap: 'wrap',
    },
  }),
  tabs: {
    minHeight: '42px',
  },
  tabButtonSpinner: (theme: Theme) => ({
    marginLeft: theme.spacing(),
  }),
  tabChip: (theme: Theme) => ({
    marginLeft: theme.spacing(1),
    color: 'inherit',
    backgroundColor: alpha(theme.palette.primary.main, 0.04),
    pointerEvents: 'none',
  }),
  tabButtonBaseRoot: {
    minHeight: 0,
    padding: '9px 16px 9px 10px',
    flexDirection: 'row',
  },
  searchContainer: (theme: Theme) => ({
    display: 'flex',
    gap: theme.spacing(),
    [theme.breakpoints.only('xs')]: {
      paddingTop: theme.spacing(),
    },
  }),
  dataGridContainer: (theme: Theme) => ({
    width: '100%',
    maxWidth: 'calc(100vw - 64px)',
    marginBottom: theme.spacing(-10),
    display: 'flex',
    flexDirection: 'column',

    '& .MuiDataGrid-root': {
      minHeight: '300px',
    },
  }),
  gridToolbar: {
    padding: 0,
    zIndex: 100,
    width: '100%',
  },
  gridToolbarContainer: (theme: Theme) => ({
    height: theme.spacing(7),
    backgroundColor: '#bcd7f2', // This is theme.palette.primary.shades['30p'] without transparency (how it would look on a white background) so it looks good when sticky during scroll
    borderRadius: theme.spacing(2, 2, 0, 0),
    padding: '7px 16px 7px 8px',
    width: '100%',
  }),
  newUserButton: {
    height: '40px',
    borderColor: 'rgba(0, 0, 0, 0.23)',
  },
  searchTextField: {
    '& .MuiInputBase-input': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  searchTextFieldInputWide: (theme: Theme) => ({
    borderRadius: theme.shape.extraLargeBorderRadius,
    width: '220px',
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shortest,
    }),
    '&.Mui-focused': {
      width: '320px',
    },
  }),
  searchTextFieldInput: (theme: Theme) => ({
    borderRadius: theme.shape.extraLargeBorderRadius,
    maxWidth: '133px',
    transition: theme.transitions.create('max-width'),
    '&.Mui-focused': {
      maxWidth: '300px',
    },
  }),
  searchIcon: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
  }),
  deviceViewActiveButton: (theme: Theme) => ({
    backgroundColor: theme.palette.primary.shades['8p'],
  }),
  deviceFilterContainer: (theme: Theme) => ({ padding: theme.spacing(1, 2, 2, 2) }),
  deviceFilterButtonContainer: (theme: Theme) => ({ padding: theme.spacing(2), marginBottom: -theme.spacing(1) }),
  treeListRoot: (theme: Theme) => ({
    marginLeft: theme.spacing(-2),
    maxHeight: 'calc(100vh - 418px - 89px)', // 100vh - sticky header - footer padding + margin
    overflowY: 'auto',
  }),
  treeList: {
    padding: 0,
  },
  treeListItem: {
    paddingRight: 0,
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  sortHeaderContainer: (theme: Theme) => ({
    padding: '10px 8px',
    paddingRight: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
  }),
  sortHeader: (theme: Theme) => ({
    color: theme.palette.text.primary,
    fontWeight: 500,
    lineHeight: theme.spacing(3),
    letterSpacing: '0.17px',
    textTransform: 'capitalize',
  }),
  linkRowIconButton: (theme: Theme) => ({
    position: 'fixed',
    margin: '-6px 0 0 -36px',
    color: theme.palette.text.secondary,
  }),
  spacingPlaceholder: (theme: Theme) => ({
    height: theme.spacing(8),
  }),
  linkRowNameContainer: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  linkRowAccountNumber: (theme: Theme) => ({
    color: theme.palette.text.secondary,
    marginTop: '5px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  }),
  countChip: (theme: Theme) => ({
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.primary.shades['4p'],
    [theme.breakpoints.down('md')]: {
      flex: 'none',
      maxWidth: 'none',
    },
  }),
  chipIcon: (theme: Theme) => ({
    display: 'flex',
    marginRight: theme.spacing(0.5),
    [theme.breakpoints.down('lg')]: {
      display: 'none',
    },
  }),
  menuButton: {
    marginTop: '-1px',
  },
  moreOptionsBtnSelected: (theme: Theme) => ({
    backgroundColor: theme.palette.primary.shades['8p'],
  }),
  menuContainer: {
    width: '40px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  menuItemWithIcon: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  qrcode: (theme: Theme) => ({
    margin: theme.spacing(3),
  }),
  qrcodeLink: (theme: Theme) => ({
    color: theme.palette.primary.dark,
    display: 'flex',
    alignItems: 'center',
  }),
  leftSpaceIcon: (theme: Theme) => ({
    marginLeft: theme.spacing(1),
  }),
  switchContainer: (theme: Theme) => ({
    marginLeft: theme.spacing(1),
  }),
  filterButton: (theme: Theme) => ({
    borderRadius: theme.shape.extraLargeBorderRadius,
  }),
  accountViewChipContainer: (theme: Theme) => ({
    display: 'inline-block',
    marginLeft: theme.spacing(1),
  }),
  accountViewMoreAbout: (theme: Theme) => ({
    color: theme.palette.primary.dark,
    textTransform: 'none',
    padding: 0,
  }),
  accountViewTabs: (theme: Theme) => ({
    padding: theme.spacing(3, 0, 2, 0),
    backgroundColor: theme.palette.common.white,
  }),
  treeViewTabs: (theme: Theme) => ({
    padding: theme.spacing(3, 0, 2, 0),
  }),
  backdrop: (theme: Theme) => ({
    color: theme.palette.common.white,
    zIndex: theme.zIndex.drawer + 1,
    backgroundColor: 'rgba(0,0,0,0.8)',
    justifyContent: 'flex-start',
    flexDirection: 'column',
  }),
  backdropSearchContainer: (theme: Theme) => ({ width: `calc(100% - ${theme.spacing(8)})`, marginTop: theme.spacing(24), maxWidth: '930px' }),
  backdropCloseIconButton: (theme: Theme) => ({ position: 'absolute', right: 0, top: 0, color: theme.palette.common.white }),
  backdropCloseIcon: { fontSize: '60px' },
}

export default useAccountStyles
